import React from "react";
import {
  Link,
  Typography,
  Box,
  CircularProgress,
  Card,
  Grid,
  Divider,
  Tooltip,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { UpliftTrackerOverview } from "../../api/transformers/uplift";
import { StatusNaming } from "../../constant/amlConstant";
import {
  upliftNamings,
  RestrictionStatusNaming,
} from "../../constant/upliftConstant";
import { fullName } from "../../utils/investor";
import { getAdviserLink } from "../../utils/adviserLinks";

interface ReviewCardProps {
  loading?: boolean | null;
  dataSource: UpliftTrackerOverview[];
}

const ReviewCard: React.FC<ReviewCardProps> = ({ dataSource, loading }) => {
  const ConsiderationTips = () => {
    return (
      <>
        <Typography>To indicate the investor has:</Typography>
        <ul style={{ listStyle: "disc", paddingLeft: "1.25rem" }}>
          <li>
            <Typography>
              <b>R</b>egular <b>C</b>ontributions
            </Typography>
          </li>
          <li>
            <Typography>
              <b>R</b>egular <b>I</b>ncome
            </Typography>
          </li>
          <li>
            <Typography>
              <b>P</b>ension <b>C</b>ommencement <b>L</b>ump <b>S</b>um
            </Typography>
          </li>
        </ul>
      </>
    );
  };

  const renderRow = (row: UpliftTrackerOverview) => {
    const topTitleStyle = {
      fontStyle: "italic",
      fontWeight: "490",
    };
    const leftTitleStyle = {
      fontStyle: "italic",
      fontWeight: "490",
      width: "25%",
    };
    const rightTitleStyle = {
      fontStyle: "italic",
      fontWeight: "490",
      width: "50%",
    };
    return (
      <Card
        key={row.investorId}
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #0000001f",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "1rem",
          marginBottom: "1rem",
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={2} md={2}>
            <Typography variant="h5" sx={topTitleStyle}>
              Investor Id
            </Typography>
            <Typography variant="body2">
              <Tooltip title="View Investor Details">
                <Link
                  href={getAdviserLink("investorDetail", {
                    personId: row.investorId,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  underline="hover"
                >
                  {row.investorId}
                </Link>
              </Tooltip>
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3} md={3}>
            <Typography variant="h5" sx={topTitleStyle}>
              Name
            </Typography>
            <Typography variant="body2">{fullName(row)}</Typography>
          </Grid>

          <Grid item xs={12} sm={3} md={3}>
            <Typography variant="h5" sx={topTitleStyle}>
              Adviser
            </Typography>
            <Typography variant="body2">{row.adviser}</Typography>
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <Typography variant="h5" sx={topTitleStyle}>
              Uplift Status
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "700" }}>
              {upliftNamings[row.upliftStatus]}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={2}
            md={2}
            sx={{ display: "grid", placeItems: "center" }}
          >
            <Typography variant="body2">
              <Link
                href={getAdviserLink("AmlProfilePage", {
                  personId: row.investorId,
                })}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
              >
                View Profile
              </Link>
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            sx={{ marginTop: "1rem", marginBottom: "1rem" }}
          >
            <Divider />
          </Grid>

          <Grid item xs={12} sm={8} md={8} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={leftTitleStyle}>
              Uplift Date
            </Typography>
            <Typography variant="body2">{row.upliftDate}</Typography>
          </Grid>
          <Grid item xs={12} sm={4} md={4} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={rightTitleStyle}>
              Considerations
              <Tooltip title={<ConsiderationTips />} placement="right">
                <HelpOutline
                  sx={{
                    color: "white",
                    backgroundColor: "primary.main",
                    borderRadius: "50%",
                    fontSize: "1rem",
                    marginLeft: "0.1rem",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </Typography>
            <Typography variant="body2">{row.considerations}</Typography>
          </Grid>

          <Grid item xs={12} sm={8} md={8} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={leftTitleStyle}>
              KYC Info
            </Typography>
            <Typography variant="body2">{row.kycInfo}</Typography>
          </Grid>
          <Grid item xs={12} sm={4} md={4} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={rightTitleStyle}>
              Deadline
            </Typography>
            <Typography variant="body2">{row.deadLineDate}</Typography>
          </Grid>

          <Grid item xs={12} sm={8} md={8} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={leftTitleStyle}>
              AML Status
            </Typography>
            <Typography variant="body2">
              <Tooltip title="View Investor AML Status">
                <Link
                  href={getAdviserLink("InvestorAmlPage", {
                    personId: row.investorId,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  underline="hover"
                >
                  {StatusNaming[row.amlStatus]}
                </Link>
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} md={4} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={rightTitleStyle}>
              Restrictions
            </Typography>
            <Typography variant="body2">
              {RestrictionStatusNaming[row.restrictions]}
            </Typography>
          </Grid>
        </Grid>
      </Card>
    );
  };

  return (
    <Box sx={{ marginTop: "2rem" }}>
      {loading ? (
        <Box
          sx={{
            minHeight: 300,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        dataSource.map(renderRow)
      )}
    </Box>
  );
};

export default ReviewCard;
